import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
} from '@mui/material';

import {
    AccountBalanceWallet,
    ReceiptLong,
    PeopleAlt,
    Savings,
    CheckCircleOutline,
    MonetizationOn,
    TrendingDown,
    Apps,
    Lightbulb,
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {[
                                { id: 'introduction', label: 'ルームシェアで家計管理が大切な理由' },
                                { id: 'app', label: '家計簿アプリの活用法' },
                                { id: 'wallet', label: '共同財布の作り方と運用術' },
                                { id: 'waste', label: '無駄遣いを防ぐコツ' },
                                { id: 'saving', label: '節約アイデア集' },
                                { id: 'tips', label: 'トラブル防止と信頼関係の築き方' },
                                { id: 'summary', label: 'まとめ' },
                            ].map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', lineHeight: 1.5 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで家計管理が大切な理由
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、家賃や光熱費を抑え、住まいをより広く・快適にできる暮らし方です。しかし、複数人で生活費を分け合うからこそ「お金の管理」がスムーズにいかないと、思わぬトラブルやストレスにつながることも。
                </Typography>
                <Typography variant="body1">
                    「家計管理さえ上手くできれば、ルームシェアの生活はもっと快適になるのに…」そんな声をよく耳にします。そこで今回は、ルームシェア経験者たちの実践ノウハウをもとに、家計簿アプリ・共同財布・無駄遣い防止・節約アイデアまで、現代的で実践的な「おすすめ家計管理法」を徹底解説します！
                </Typography>
            </Box>

            {/* 家計簿アプリの活用法 */}
            <Box id="app" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Apps sx={{ mr: 1, color: 'primary.main' }} />
                        家計簿アプリの活用法
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    紙の家計簿や手書きノートも根強い人気ですが、ルームシェアでは「共有」と「透明性」が重要。その点で、スマホ家計簿アプリはまさに救世主です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: { xs: 2, md: 0 } }}>
                            <Typography variant="h6" gutterBottom>
                                家計簿アプリを使うメリット
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="支出をリアルタイムで全員が確認できる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="自動で集計・グラフ化されて分かりやすい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="記録漏れや不公平感を防げる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                共有に強い家計簿アプリ３選
                            </Typography>
                            <List>
                                {/* 1. マネーフォワード ME */}
                                <ListItem>
                                    <ListItemIcon><ReceiptLong color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="マネーフォワード ME"
                                        secondary="シェアボードで最大2人まで口座＆支出をリアルタイム共有（プレミアム要）"
                                    />
                                </ListItem>
                                {/* 2. Zaim */}
                                <ListItem>
                                    <ListItemIcon><ReceiptLong color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="Zaim"
                                        secondary="ペア家計簿でパートナーと相互共有。自動レシート読取＋共同貯金プラン機能付き"
                                    />
                                </ListItem>
                                {/* 3. OshidOri */}
                                <ListItem>
                                    <ListItemIcon><ReceiptLong color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="OshidOri（オシドリ）"
                                        secondary="グループ作成→メンバー招待だけで共有完了。精算機能とリマインダーで割り勘もラクラク"
                                    />
                                </ListItem>
                            </List>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">運用のポイント</Typography>
                                <ul>
                                    <li>全員をアプリ内グループに招待 → 即、支出共有スタート</li>
                                    <li>カテゴリーは固定（家賃・光熱費・食費・雑費） → 集計がブレない</li>
                                    <li>支払い後すぐ入力 → 未精算を防止</li>
                                    <li>月末にアプリ内「自動精算」 or 「精算レポート」機能でまとめて相殺</li>
                                </ul>
                            </Box>
                        </Paper>
                    </Grid>

                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/sharehouse-app_roomshare-osusume-kakeikanrihou.jpg"
                        alt="明るいリビングでルームメイトがスマホ家計簿アプリを操作しながら、費用項目を相談している様子。全員が画面を見ながら意見交換しており、協力的な雰囲気が伝わる画像。インテリアは北欧風、全体にリラックスしたムード"
                        style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    ※アプリは全員が同じものを使い、支払い・記録ルールを統一しておくのがコツ。「何にいくら使ったか」が可視化され、曖昧さや不満が減ります。
                </Typography>
            </Box>

            {/* 共同財布の作り方と運用術 */}
            <Box id="wallet" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccountBalanceWallet sx={{ mr: 1, color: 'success.main' }} />
                        共同財布の作り方と運用術
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家賃や光熱費のような大きな支払いはもちろん、日用品や食材などの「細かい共同支出」もルームシェア生活の難所。そこで役立つのが「共同財布」の導入です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="h6" gutterBottom>
                                共同財布のつくり方
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="action" /></ListItemIcon>
                                    <ListItemText
                                        primary="現金派："
                                        secondary="100円ショップのチャック袋や封筒、キャッシュトレイなどを利用し、月初にみんなで一定額（例：各自5,000円）を入れる"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><MonetizationOn color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="キャッシュレス派："
                                        secondary="みんなが使えるプリペイドカード、PayPayやLINE Payのグループ送金機能、バーチャル口座などを利用"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Savings color="success" /></ListItemIcon>
                                    <ListItemText
                                        primary="家計簿アプリと連携："
                                        secondary="共同財布の出入りをアプリで記録・精算し、不透明な支出を防ぐ"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

                        </Box>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                    ポイント：共同財布を続けるコツ
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                        <ListItemText primary="精算日や金額など、ルールを紙やLINEグループで全員に明示する" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                        <ListItemText primary="支出時は必ずレシートを保管・写真で記録" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                        <ListItemText primary="使い道に迷う時は、必ず事前に相談する習慣を" />
                    </ListItem>
                </List>
            </Box>

            {/* 無駄遣いを防ぐコツ */}
            <Box id="waste" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingDown sx={{ mr: 1, color: 'warning.main' }} />
                        無駄遣いを防ぐコツ
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    お金のトラブルで多いのが「誰かがこっそり多めに使ってしまう」「気づかぬうちに予算オーバー」などの“無駄遣い問題”。日々のちょっとした工夫で大きく防げます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fffde7' }}>
                    <Typography variant="h6" gutterBottom>
                        無駄遣い防止のチェックポイント
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="「共同のもの」と「個人のもの」を明確に区別" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="まとめ買いは月1回・週1回など頻度を決める" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="買い物リストをあらかじめ作っておく" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="“ついで買い”防止に、購入前の相談を徹底" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="使途不明の支出が出た場合は必ず共有し、原因を話し合う" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/waste-prevention_roomshare-osusume-kakeikanrihou.jpg"
                        alt="コルクボードに『共同のものリスト』と『個人のものリスト』が色分けされた付箋で貼られ、ルームメイトたちがリストを指さしながら和やかに相談している場面。明るい日差しの入るダイニングスペースで、計画的な買い物の様子が伝わる"
                        style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    ルールを決めても守られない場合は、責めるのではなく「なぜそうなったか」をみんなで話し合う姿勢が大切です。
                </Typography>
            </Box>

            {/* 節約アイデア集 */}
            <Box id="saving" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Lightbulb sx={{ mr: 1, color: 'secondary.main' }} />
                        節約アイデア集
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアならではの「みんなでやるから節約できる」工夫を集めました。楽しみながらお金をセーブして、生活の質もアップしましょう！
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    みんなで食材をまとめ買い
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    近所のスーパーやネット通販で、調味料・米・冷凍食品などを大袋で購入。その場で割り勘すれば、ひとり暮らしよりずっとお得！
                                </Typography>
                                <Chip label="まとめ買い" color="primary" variant="outlined" sx={{ mr: 1 }} />
                                <Chip label="食費節約" color="success" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    共同で調理・おかずシェア
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    週末は交代で料理担当を決めて、みんなで家庭料理を作る。作り置きやお弁当分までシェアすれば、外食やコンビニ利用も減らせる！
                                </Typography>
                                <Chip label="自炊" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                                <Chip label="健康的" color="success" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    不用品をみんなでシェア・リサイクル
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    使わなくなった食器や家電は、まずはルームメイト間で譲り合い。フリマアプリで共同出品して売上を共有するのもおすすめ。
                                </Typography>
                                <Chip label="リサイクル" color="info" variant="outlined" sx={{ mr: 1 }} />
                                <Chip label="エコ" color="success" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    光熱費もグループで節約チャレンジ
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    エアコンや照明のつけっぱなしをやめて、月ごとに目標金額を設定。達成したら、ちょっとしたご褒美イベントを開催するのも楽しい！
                                </Typography>
                                <Chip label="光熱費" color="warning" variant="outlined" sx={{ mr: 1 }} />
                                <Chip label="ゲーム感覚" color="primary" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/share-cooking_roomshare-osusume-kakeikanrihou.jpg"
                        alt="広々とした共有キッチンでルームメイトたちが協力して食事を作り、談笑しながら盛り付けをしている光景。カラフルな食材と明るい照明、親しみやすい雰囲気が伝わり、共同生活の節約と楽しさを象徴する場面"
                        style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                    />
                </Box>
            </Box>

            {/* トラブル防止と信頼関係の築き方 */}
            <Box id="tips" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    トラブル防止と信頼関係の築き方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家計管理のコツは「お金の話をオープンにすること」と「みんなの納得感を大事にすること」。どんなに気が合う仲間でも、価値観や金銭感覚が違えばズレは生まれます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#e1f5fe' }}>
                    <Typography variant="h6" gutterBottom>
                        トラブルを防ぐための心得
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="info" /></ListItemIcon>
                            <ListItemText primary="費用分担やルールは最初にきちんと話し合い、紙やデジタルで記録" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="info" /></ListItemIcon>
                            <ListItemText primary="定期的な『家計会議』で収支や不満を共有" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="info" /></ListItemIcon>
                            <ListItemText primary="支払い遅延や領収書の未提出は、早めにリマインドする" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="info" /></ListItemIcon>
                            <ListItemText primary="困った時は、責任を押し付けず『どう解決するか』を一緒に考える" />
                        </ListItem>
                    </List>
                </Paper>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの家計管理は、ちょっとした工夫とオープンなコミュニケーションがあれば、驚くほど快適なものになります。アプリや共同財布で「見える化」し、みんなで協力して節約や無駄遣い防止に取り組むことで、信頼関係も深まります。
                </Typography>
                <Typography variant="body1">
                    ぜひ今日から、あなたのシェア生活にも「おすすめ家計管理法」を取り入れてみてください。きっと、今よりもっと安心で楽しいルームシェアが実現できるはずです！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-osusume-kakeikanrihou',
    slug: 'roomshare-osusume-kakeikanrihou',
    title: 'ルームシェアの「おすすめ家計管理法」',
    excerpt: '家計簿アプリの活用、共同財布の作り方、無駄遣い防止術、節約アイデアなど、ルームシェアで役立つ家計管理の実践的ノウハウを紹介します。',
    coverImage: '/images/blog/cover-roomshare-money_roomshare-osusume-kakeikanrihou.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが食事や会話を楽しみつつ、家計簿アプリや共同財布を使って協力し合う様子が伝わる、親しみやすく温かな雰囲気の写真',
    publishedAt: '2024-06-21T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '家計管理', '節約術'],
    tags: ['家計簿アプリ', '共同財布', '無駄遣い防止', '節約アイデア', 'シェアハウス'],
    author: {
        name: '小林 智子',
        avatar: '/images/avatars/tomoko-kobayashi.jpg',
        bio: '首都圏のシェアハウス運営歴10年以上。実体験に基づいた家計管理やトラブル対策の知見に定評あり。ルームシェアの魅力と実践ノウハウを発信中。',
        profilePicture: '/images/avatars/tomoko-kobayashi.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;